import { useAxios, useHealthblocksSWR, useSubjectId } from '@healthblocks-io/core'
import { isPast } from 'date-fns/esm'
import { useCallback } from 'react'

import { useAxiosAsync } from '@/@bothrs-react/hooks/useAxiosAsync'
import { isDocumentValid } from '../helpers/document.helpers'

import type { AxiosResponse } from 'axios'
import type { SignableDocument } from '../types/documents.types'

export function useDocumentFlows() {
  const subjectId = useSubjectId()
  return useHealthblocksSWR<SignableDocument[]>(
    subjectId
      ? '/document-flow?' +
          new URLSearchParams({
            subjectId: subjectId.toString(),
            include: 'activities,version,activities.definition.fields,activities.document.file,activities.inputs',
          })
      : null,
  )
}

export function isSignableDocumentFlow(flow: SignableDocument) {
  return flow.status !== 'signed' && (!flow.expiresAt || !isPast(new Date(flow.expiresAt))) && isDocumentValid(flow)
}

function useSignableDocuments() {
  const hbAxios = useAxios()
  const subjectId = useSubjectId()

  // Methods
  // ------------------------------------------------------------------------- /
  const getSignableDocuments = useCallback(() => {
    return hbAxios
      .get<SignableDocument[]>(`/document-flow`, {
        params: {
          include: 'activities,version,activities.definition.fields,activities.document.file,activities.inputs',
          subjectId: subjectId,
        },
      })
      .then((response) => {
        return {
          ...response,
          data: [...response.data]
            .filter((document) => !(document?.expiresAt && isPast(new Date(document.expiresAt))))
            .filter((document) => isDocumentValid(document)),
        }
      })
  }, [subjectId, hbAxios])

  const getSignableDocumentDetails = useCallback(
    (id: number) => {
      return hbAxios.get(`/document-flow/${id}`, {
        params: {
          include: 'version,activities.definition.fields,activities.definition.file,activities.inputs',
          subjectId: subjectId,
        },
      })
    },
    [hbAxios, subjectId],
  )

  const readSignableDocument = useCallback(
    (id: number) => {
      return hbAxios.patch(`/document-flow/${id}`, {
        status: 'read',
      })
    },
    [hbAxios],
  )

  const updateSignableDocumentActivity = useCallback(
    (activityId: number, body: unknown) => {
      return hbAxios.patch(`/document-activity/${activityId}`, body)
    },
    [hbAxios],
  )

  const getSignableDocumentActivityPreview = useCallback(
    (activityId: number) => {
      return hbAxios
        .get<Blob>(`/document-activity/${activityId}/render`, {
          responseType: 'blob',
        })
        .then((response) => {
          return {
            ...response,
            data: URL.createObjectURL(response.data),
          }
        })
    },
    [hbAxios],
  )

  const getSignedDocumentActivityPreview = useCallback(
    (activityId: number) => {
      return hbAxios
        .get<Blob>(`/views/patient/documents/document-activity/${activityId}/preview`, {
          responseType: 'blob',
          params: { subjectId },
        })
        .then((response) => {
          return {
            ...response,
            data: URL.createObjectURL(response.data),
          }
        })
    },
    [hbAxios, subjectId],
  )

  const signDocument = useCallback(
    (documentId: number) => {
      return hbAxios.patch(`/document-flow/${documentId}`, {
        status: 'signed',
      })
    },
    [hbAxios],
  )

  const getSignableDocumentsForToday = useCallback(() => {
    return getSignableDocuments().then((value: AxiosResponse<SignableDocument[]>) => {
      return value?.data
        ? {
            ...value,
            data: value?.data?.filter(
              (document) => isPast(new Date(document.createdAt)) && document.status !== 'signed',
            ),
          }
        : value
    })
  }, [getSignableDocuments])

  return {
    getSignableDocuments: useAxiosAsync(getSignableDocuments),
    getSignableDocumentDetails: useAxiosAsync(getSignableDocumentDetails),
    readSignableDocument: useAxiosAsync(readSignableDocument),
    updateSignableDocumentActivity: useAxiosAsync(updateSignableDocumentActivity),
    getSignableDocumentActivityPreview: useAxiosAsync(getSignableDocumentActivityPreview),
    getSignedDocumentActivityPreview: useAxiosAsync(getSignedDocumentActivityPreview),
    signDocument: useAxiosAsync(signDocument),
    getSignableDocumentsForToday: useAxiosAsync(getSignableDocumentsForToday),

    signableDocumentsFetcher: getSignableDocuments,
    signableDocumentsDetailsFetcher: getSignableDocumentDetails,
    readSignableDocumentFetcher: readSignableDocument,
    updateSignableDocumentActivityFetcher: updateSignableDocumentActivity,
    signableDocumentActivityPreviewFetcher: getSignableDocumentActivityPreview,
    signDocumentFetcher: signDocument,
    signableDocumentsForTodayFetcher: getSignableDocumentsForToday,
  }
}

export { useSignableDocuments }
